import ActionButton from '../../../../components/Buttons/ActionButton';
import ToggleButtonGroup, {
  ToggleOption,
} from '../../../../components/Forms/Buttons/ToggleButtonGroup';
import DatePicker from '../../../../components/Forms/DatePicker/DatePicker';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';

import {
  ReservationFilters,
  ReservationStatus,
} from '../../../../types/restaurant/RestaurantReservationTypes';

interface Props {
  filters: ReservationFilters;
  tables: Array<{ id: number; name: string; code: string }>;
  onChange: (values: ReservationFilters) => void;
  onRetry: () => void;
}

const activityOptions: ToggleOption<ReservationFilters['activity']>[] = [
  {
    value: 'ACTIVE',
    label: 'Activas',
  },
  {
    value: 'HISTORY',
    label: 'Historial',
  },
  {
    value: 'ALL',
    label: 'Todas',
  },
];

const ReservationFiltersBar = ({
  filters,
  tables,
  onChange,
  onRetry,
}: Props) => (
  <div className="border-b border-stroke bg-gray-2/40 p-5 dark:border-strokedark dark:bg-boxdark-2/30 sm:px-7">
    <ToggleButtonGroup
      value={filters.activity}
      options={activityOptions}
      onChange={(activity) =>
        onChange({
          ...filters,
          activity,
        })
      }
      className="mb-4"
    />

    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(220px,1.5fr)_repeat(4,minmax(145px,1fr))_auto]">
      <SearchInputField
        label="Buscar"
        name="reservationSearch"
        value={filters.search ?? ''}
        placeholder="Nombre, teléfono o número"
        withMargin={false}
        onChange={(e) =>
          onChange({
            ...filters,
            search: e.target.value,
          })
        }
      />

      <SelectField
        label="Estado"
        name="reservationStatus"
        value={filters.status ?? ''}
        iconKey="status"
        withMargin={false}
        options={[
          { value: '', label: 'Todos los estados' },
          { value: 'PENDING', label: 'Pendientes' },
          { value: 'CONFIRMED', label: 'Confirmadas' },
          { value: 'SEATED', label: 'Recibidas' },
          { value: 'COMPLETED', label: 'Completadas' },
          { value: 'CANCELLED', label: 'Canceladas' },
          { value: 'NO_SHOW', label: 'No-show' },
        ]}
        onChange={(e) =>
          onChange({
            ...filters,
            status: e.target.value as ReservationStatus | '',
          })
        }
      />

      <DatePicker
        label="Desde"
        name="reservationDateFrom"
        value={filters.dateFrom ?? ''}
        iconKey="calendar"
        withMargin={false}
        onChange={(value) =>
          onChange({
            ...filters,
            dateFrom: value,
          })
        }
      />

      <DatePicker
        label="Hasta"
        name="reservationDateTo"
        value={filters.dateTo ?? ''}
        iconKey="calendar"
        withMargin={false}
        onChange={(value) =>
          onChange({
            ...filters,
            dateTo: value,
          })
        }
      />

      <SelectField
        label="Mesa"
        name="reservationTable"
        value={filters.tableId ?? ''}
        iconKey="table"
        withMargin={false}
        options={[
          {
            value: '',
            label: 'Todas las mesas',
          },
          ...tables.map((table) => ({
            value: table.id,
            label: `${table.code} · ${table.name}`,
          })),
        ]}
        onChange={(e) =>
          onChange({
            ...filters,
            tableId: e.target.value ? Number(e.target.value) : '',
          })
        }
      />

      <div>
        <label className="mb-2.5 block text-sm font-medium opacity-0">
          Acción
        </label>

        <ActionButton
          label="Actualizar"
          variant="primary"
          size="md"
          fullWidth={false}
          onClick={onRetry}
          className="h-[50px] whitespace-nowrap"
        />
      </div>
    </div>

    {(filters.dateFrom || filters.dateTo) && (
      <button
        type="button"
        onClick={() =>
          onChange({
            ...filters,
            dateFrom: '',
            dateTo: '',
          })
        }
        className="mt-3 text-sm font-semibold text-primary hover:underline"
      >
        Limpiar rango de fechas
      </button>
    )}
  </div>
);

export default ReservationFiltersBar;